import React from "react";

import { Button, InfoMessage, PasswordField } from "@amphore-dev/amphore-lib";
import { AxiosError } from "axios";
import { Form, Formik } from "formik";
import { t } from "i18next";
import { Link, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { useMutation } from "@tanstack/react-query";

import AuthService from "@/services/AuthService";

import { getApiErrormessage } from "@/utils/UAPIErrors";
import { fieldError, passwordSchema } from "@/utils/UForms";

import { TApiError } from "@/types";

type TValues = { password: string; confirmPassword: string };

/**
 * /reset-password?token=: the new password, from the mailed link. Every
 * session is signed out on the way, so the next step is the login page.
 */
export const ResetPassword: React.FC = () => {
	const [params] = useSearchParams();
	const token = params.get("token") ?? "";

	const reset = useMutation<void, AxiosError<TApiError>, TValues>({
		mutationFn: (values) =>
			AuthService.resetPassword(
				token,
				values.password,
				values.confirmPassword
			),
	});

	if (!token)
		return (
			<InfoMessage color="danger">{t("account.invalidLink")}</InfoMessage>
		);

	if (reset.isSuccess)
		return (
			<div className="flex flex-col gap-4" data-testid="reset-done">
				<InfoMessage color="success">
					{t("account.reset.done")}
				</InfoMessage>
				<Link to="/login" className="text-center">
					{t("auth.signIn")}
				</Link>
			</div>
		);

	return (
		<Formik<TValues>
			initialValues={{ password: "", confirmPassword: "" }}
			validationSchema={Yup.object({
				password: passwordSchema(),
				confirmPassword: Yup.string()
					.required(t("forms.password.required"))
					.oneOf([Yup.ref("password")], t("forms.password.match")),
			})}
			onSubmit={(values) =>
				reset.mutateAsync(values).catch(() => undefined)
			}
		>
			{(formik) => {
				const { values, setFieldValue, isSubmitting } = formik;
				return (
					<Form className="flex flex-col gap-4">
						<p className="text-sm text-neutral-600">
							{t("account.reset.intro")}
						</p>
						<PasswordField
							label={t("forms.password.new")}
							name="password"
							error={fieldError(formik, "password")}
							autoComplete="new-password"
							value={values.password}
							onChange={(value) =>
								setFieldValue("password", value)
							}
						/>
						<PasswordField
							label={t("forms.password.confirm")}
							name="confirmPassword"
							error={fieldError(formik, "confirmPassword")}
							autoComplete="new-password"
							value={values.confirmPassword}
							onChange={(value) =>
								setFieldValue("confirmPassword", value)
							}
						/>
						{reset.error && (
							<InfoMessage color="danger">
								{getApiErrormessage(reset.error)}
								{reset.error.response?.status === 400 && (
									<>
										{" "}
										<Link to="/forgot-password">
											{t("account.reset.askAgain")}
										</Link>
									</>
								)}
							</InfoMessage>
						)}
						<Button type="submit" isLoading={isSubmitting}>
							{t("account.reset.submit")}
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};
